/**
 * D2 — Inline diff view for virtual files.
 * Uses a simple LCS-based line diff to avoid external dependencies.
 */
import { useTranslation } from "react-i18next"

import type { DeepFile } from "@/stores/ai-explorer-store"

import { cn } from "@/lib/utils"

// ── Minimal line-diff implementation ─────────────────────────────────────────

type DiffLine =
  | { type: "equal"; text: string }
  | { type: "added"; text: string }
  | { type: "removed"; text: string }

function diffPrefix(type: DiffLine["type"]): string {
  if (type === "added") return "+"
  if (type === "removed") return "-"
  return " "
}

/**
 * Compute a line-by-line diff using a greedy LCS approach.
 * Good enough for scratchpad files (not git-level quality, but correct).
 */
function diffLines(oldText: string, newText: string): DiffLine[] {
  const oldLines = oldText.split("\n")
  const newLines = newText.split("\n")

  // Build LCS table
  const m = oldLines.length
  const n = newLines.length
  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array<number>(n + 1).fill(0))

  for (let i = m - 1; i >= 0; i--) {
    for (let j = n - 1; j >= 0; j--) {
      if (oldLines[i] === newLines[j]) {
        dp[i]![j] = (dp[i + 1]?.[j + 1] ?? 0) + 1
      } else {
        dp[i]![j] = Math.max(dp[i + 1]?.[j] ?? 0, dp[i]?.[j + 1] ?? 0)
      }
    }
  }

  // Trace back
  const result: DiffLine[] = []
  let i = 0
  let j = 0
  while (i < m || j < n) {
    if (i < m && j < n && oldLines[i] === newLines[j]) {
      result.push({ type: "equal", text: oldLines[i] ?? "" })
      i++
      j++
    } else if (j < n && (i >= m || (dp[i + 1]?.[j] ?? 0) <= (dp[i]?.[j + 1] ?? 0))) {
      result.push({ type: "added", text: newLines[j] ?? "" })
      j++
    } else {
      result.push({ type: "removed", text: oldLines[i] ?? "" })
      i++
    }
  }
  return result
}

// ── Component ─────────────────────────────────────────────────────────────────

interface FileDiffViewProps {
  file: DeepFile
}

export function FileDiffView({ file }: FileDiffViewProps) {
  const { t } = useTranslation()
  const previous = file.previous_content ?? ""
  const current = file.content

  const diff = diffLines(previous, current)
  const hasChanges = diff.some((d) => d.type !== "equal")

  if (!hasChanges) {
    return <p className="text-muted-foreground p-4 text-sm">{t("deepExplorer.no_changes")}</p>
  }

  return (
    <div
      aria-label={t("deepExplorer.file_diff_aria")}
      className="overflow-auto font-mono text-xs"
      role="region"
    >
      {diff.map((line, i) => (
        <div
          key={i}
          className={cn(
            "px-3 py-[1px] leading-5 break-all whitespace-pre-wrap",
            line.type === "added" && "bg-success/15",
            line.type === "removed" && "bg-destructive/15 line-through opacity-70",
            line.type === "equal" && "text-foreground"
          )}
        >
          <span
            aria-hidden="true"
            className={cn(
              "mr-2 opacity-50 select-none",
              line.type === "added" && "text-success",
              line.type === "removed" && "text-destructive"
            )}
          >
            {diffPrefix(line.type)}
          </span>
          {line.text}
        </div>
      ))}
    </div>
  )
}
